function toKebab(t: string) {
    return t
        .split("")
        .map((letter, index) => {
            if (/[A-Z]/.test(letter)) {
                return ` ${letter.toLowerCase()}`;
            }
            return letter;
        })
        .join("")
        .replace(/_/g, "-")
        .replace(/\s+/g, "-")
        .replace(/[_\s]+/g, "-");
}

export function toTitle(t: string) {
    return toKebab(t)
        .split("-")
        .map((word) => {
            return word.slice(0, 1).toUpperCase() + word.slice(1);
        })
        .join(" ");
}

/**
 * Parses event parameter string and extracts both parameter key and type
 * @param input - Input string in format ep_${param_key} or ep_${type}_${param_key}
 * @returns Object containing param_key and type (defaults to 'text' if type is not specified)
 *
 * @example
 * parseEventParam("ep_difficulty_tag") // returns { param_key: "difficulty_tag", type: "text" }
 * parseEventParam("ep_string_difficulty_tag") // returns { param_key: "difficulty_tag", type: "string" }
 * parseEventParam("ep_number_level_id") // returns { param_key: "level_id", type: "number" }
 */
/**
 * Extracts type from event parameter string
 * @param input - Input string in format ep_${param_key} or ep_${type}_${param_key}
 * @returns Type string - defaults to 'string' if type is not specified
 *
 * @example
 * getEventParamType("ep_difficulty_tag") // returns "string"
 * getEventParamType("ep_string_difficulty_tag") // returns "string"
 * getEventParamType("ep_number_level_id") // returns "number"
 */
export function getEventParamType(input: string): string | null {
    // Check if the input starts with "ep_"
    if (!input.startsWith("ep_")) {
        return null;
    }

    // Remove the "ep_" prefix
    const withoutPrefix = input.substring(3);

    // Split by underscores
    const parts = withoutPrefix.split("_");

    // If there are multiple parts, check if first part is "string" or "number"
    if (parts.length >= 2) {
        const possibleType = parts[0];
        if (possibleType === "string" || possibleType === "number") {
            return possibleType;
        }
    }

    // Default to "string" type
    return "string";
}

export function parseEventParam(input: string): { param_key: string; type: string } {
    // Check if the input starts with "ep_"
    if (!input.startsWith("ep_")) {
        return { param_key: input, type: "string" };
    }

    // Remove the "ep_" prefix
    const withoutPrefix = input.substring(3);

    // Split by underscores
    const parts = withoutPrefix.split("_");

    // If there's only one part, it's just the param_key
    if (parts.length === 1) {
        return { param_key: parts[0], type: "string" };
    }

    // If there are multiple parts, check if first part is "string" or "number"
    if (parts.length >= 2) {
        const possibleType = parts[0];
        if (possibleType === "string" || possibleType === "number") {
            // First part is a valid type, rest is param_key
            const paramKey = parts.slice(1).join("_");
            return { param_key: paramKey, type: possibleType };
        }
    }

    // Entire string is param_key with default type "string"
    return { param_key: withoutPrefix, type: "string" };
}

/**
 * Formats a string with underscores to title case
 * Example: "metric_category" -> "Metric Category"
 */
export function formatUnderscoreToTitle(text: string): string {
    return text
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}
